import { ChatMessageSendProblem } from '@/features/ChatDialog/SendMessage/ChatMessageSendProblem';
import { ChatMessageHistoryButton } from '@/features/ChatDialog/EditMessage/ChatMessageHistoryButton';
import { ChatMessageContextMenu } from '@/features/ChatDialog/EditMessage/ChatMessageContextMenu';
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { formatMoscowCalendarDay, getMoscowDayKey, formatMoscowTime } from '@/shared/lib/dateTime';
import {
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';

import classNames from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { ApiChatReadState, ApiChatMessageKind, ApiChatMessageSendStatus } from '@/core/generated/api';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import {
    getChatDialogDisplayName,
    getChatDialogIncomingReadBoundary,
    getChatDialogReadBoundary,
    hasUnreadChatDialogMessages,
} from '@/entities/ChatDialog/model/selectors';

import { getChatDialogDisplayMessages } from '@/entities/ChatDialog/model/displayMessages';
import { getChatDialogMessageText } from '@/entities/ChatDialog/model/salebotServiceEvent';
import { ChatDialogComposer } from '@/features/ChatDialog/SendMessage/ChatDialogComposer';
import type { ChatReplyTemplateMenuHandle } from '@/features/ChatReplyTemplates/types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { CopyButton } from '@/shared/ui/CopyButton/CopyButton';
import { MessageText } from '@/shared/ui/MessageText/MessageText';
import { RequestEmpty, RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import { useChatDialogRead } from '../useChatDialogRead';
import { ChatDialogAttachment } from './ChatDialogAttachment';
import classes from '../ChatDialogPage.module.scss';

interface ChatDialogConversationProps {
    /** Действия выбранной переписки */
    headerActions?: ReactNode;
    historyOwnerAppUserId?: string;

    /** Разрешает подтверждать прочтение */
    canRead: boolean;

    /** Разрешает отправлять ответы */
    canSend: boolean;

    /** Возвращает к списку диалогов */
    onClose: () => void;

    /** Адрес выбранной переписки */
    shareUrl: string;
}

/** Показывает историю выбранного диалога */
export const ChatDialogConversation = (
    { canRead, canSend, onClose, shareUrl, headerActions, historyOwnerAppUserId }: ChatDialogConversationProps
) => {
    const workspace = useChatDialogWorkspace(useShallow((state) => ({
        dialog: state.selectedDialog,
        messages: state.messages,
        nextBeforeSequence: state.nextBeforeSequence,
        nextAfterSequence: state.nextAfterSequence,
        messagesStatus: state.messagesStatus,
        messagesError: state.messagesError,
        historyStatus: state.historyStatus,
        historyError: state.historyError,
        readStatus: state.readStatus,
        readError: state.readError,
        fetchMessages: state.fetchMessages,
        fetchOlderMessages: state.fetchOlderMessages,
        fetchNewerMessages: state.fetchNewerMessages,
        markRead: state.markRead,
        refreshLive: state.refreshLive,
    })));

    const viewportRef = useRef<HTMLDivElement>(null);
    const templateMenuRef = useRef<ChatReplyTemplateMenuHandle>(null);
    const readTargetRef = useRef<HTMLSpanElement>(null);
    const initialScrollDone = useRef(false);
    const pinnedToBottom = useRef(true);
    const lastMessageId = useRef<string | undefined>();
    const loadingOlder = useRef(false);
    const lastScrollTop = useRef(0);
    const scrollAnchor = useRef<{ height: number; top: number } | null>(null);
    const manuallyUnread = Boolean(workspace.dialog?.isManuallyUnread);

    const boundary = useMemo(() => manuallyUnread
        ? getChatDialogReadBoundary(workspace.messages)
        : getChatDialogIncomingReadBoundary(workspace.messages), [manuallyUnread, workspace.messages]);

    const displayMessages = useMemo(() => getChatDialogDisplayMessages(
        workspace.messages,
        workspace.dialog?.channel?.id,
    ), [workspace.messages, workspace.dialog?.channel?.id]);

    const displayBoundary = useMemo(() => manuallyUnread
        ? getChatDialogReadBoundary(displayMessages)
        : getChatDialogIncomingReadBoundary(displayMessages), [manuallyUnread, displayMessages]);

    const isLoadingHistory = workspace.historyStatus === RequestStatus.Loading;

    useLayoutEffect(() => {
        const viewport = viewportRef.current;

        if (!viewport) return;

        if (workspace.messagesStatus === RequestStatus.Loading) {
            initialScrollDone.current = false;
            scrollAnchor.current = null;

            return;
        }

        if (workspace.messagesStatus !== RequestStatus.Success) return;

        if (!initialScrollDone.current) {
            pinnedToBottom.current = workspace.nextAfterSequence === null;
            viewport.scrollTop = pinnedToBottom.current
                ? viewport.scrollHeight
                : 0;
            initialScrollDone.current = true;
        } else if (scrollAnchor.current && !isLoadingHistory) {
            viewport.scrollTop = scrollAnchor.current.top + viewport.scrollHeight - scrollAnchor.current.height;
            scrollAnchor.current = null;
        }

        const latest = workspace.messages[workspace.messages.length - 1];

        if (pinnedToBottom.current && latest?.id !== lastMessageId.current) {
            viewport.scrollTop = viewport.scrollHeight;
        }

        lastMessageId.current = latest?.id;
    }, [isLoadingHistory, workspace.messages, workspace.messagesStatus, workspace.nextAfterSequence]);

    useChatDialogRead({
        boundary,
        enabled: canRead && (manuallyUnread || hasUnreadChatDialogMessages(workspace.messages))
            && workspace.messagesStatus === RequestStatus.Success
            && workspace.readStatus !== RequestStatus.Error,
        targetRef: readTargetRef,
        viewportRef,
        onRead: workspace.markRead,
    });

    /** Загружает старую историю, сохраняя место чтения */
    const loadOlderMessages = async (): Promise<void> => {
        const cannotLoadHistory = loadingOlder.current || isLoadingHistory
            || workspace.nextBeforeSequence === null
            || workspace.messagesStatus !== RequestStatus.Success;

        if (cannotLoadHistory) return;

        const viewport = viewportRef.current;

        if (viewport) scrollAnchor.current = {
            height: viewport.scrollHeight,
            top: viewport.scrollTop
        };
        loadingOlder.current = true;

        try {
            await workspace.fetchOlderMessages();
        } finally {
            loadingOlder.current = false;
        }
    };

    return (
        <section
            className={classes.conversationPanel}
            aria-label="Переписка"
        >
            <header className={classes.conversationHeader}>
                <ActionButton
                    type="button"
                    className={classes.backButton}
                    label="К диалогам"
                    icon="pi pi-arrow-left"
                    color="secondary"
                    onClick={onClose}
                />
                <div>
                    <h2>
                        {workspace.dialog
                            ? getChatDialogDisplayName(workspace.dialog)
                            : 'Выберите диалог'}
                    </h2>
                    {workspace.dialog && (
                        <p className={classes.contactMetadata}>
                            {workspace.dialog.channel?.name}
                            {workspace.dialog.accountName && ` · ${workspace.dialog.accountName}`}
                            {workspace.dialog.messengerUserId && ` · ID: ${workspace.dialog.messengerUserId}`}
                        </p>
                    )}
                    {!canRead && workspace.dialog &&
                        <p className={classes.contactMetadata}>Просмотр без отметки прочтения</p>
                    }
                </div>
                {workspace.dialog?.id && (
                    <CopyButton
                        value={shareUrl}
                        label="Копировать ссылку на диалог"
                        successMessage="Ссылка на диалог скопирована"
                    />
                )}
                {headerActions}
            </header>

            <div
                ref={viewportRef}
                onContextMenu={(event) => {
                    if (event.target === event.currentTarget) templateMenuRef.current?.show(event);
                }}
                onScroll={(event) => {
                    const viewport = event.currentTarget;
                    const movingUp = viewport.scrollTop < lastScrollTop.current;
                    lastScrollTop.current = viewport.scrollTop;

                    if (movingUp && viewport.scrollTop <= 160
                        && workspace.historyStatus !== RequestStatus.Error) {
                        void loadOlderMessages();
                    }

                    pinnedToBottom.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
                }}
                className={classes.messages}
                role="log"
                aria-live="polite"
                aria-relevant="additions text"
            >
                {!workspace.dialog && (
                    <RequestEmpty message="Выберите диалог, чтобы прочитать сообщения" />
                )}
                {workspace.messagesStatus === RequestStatus.Loading && (
                    <RequestLoading message="Загружаем историю…" />
                )}
                <RequestErrorBanner
                    status={workspace.messagesStatus}
                    error={workspace.messagesError}
                    fallbackMessage="Не удалось загрузить историю"
                    onRetry={workspace.fetchMessages}
                />
                {workspace.messagesStatus === RequestStatus.Success && (
                    <>
                        {workspace.nextBeforeSequence !== null && (
                            <div className={classes.pagination}>
                                <ActionButton
                                    type="button"
                                    label="Старые сообщения"
                                    color="secondary"
                                    loading={isLoadingHistory}
                                    disabled={isLoadingHistory}
                                    onClick={loadOlderMessages}
                                />
                            </div>
                        )}
                        <RequestErrorBanner
                            status={workspace.historyStatus}
                            error={workspace.historyError}
                            fallbackMessage="Не удалось загрузить старые сообщения"
                            onRetry={loadOlderMessages}
                        />
                        {workspace.messages.length === 0 && (
                            <RequestEmpty message="В этом диалоге пока нет сообщений" />
                        )}
                        {displayMessages.map((message, index) => (
                            <Fragment key={message.id}>
                                {index === 0 || getMoscowDayKey(Date.parse(message.sentAt || message.receivedAt || ''))
                                    !== getMoscowDayKey(Date.parse(displayMessages[index - 1].sentAt
                                        || displayMessages[index - 1].receivedAt || ''))
                                    ? (
                                        <div className={classes.dayDivider}>
                                            <span>
                                                {formatMoscowCalendarDay(Date.parse(message.sentAt || message.receivedAt || ''))}
                                            </span>
                                        </div>
                                    )
                                    : null}
                                <ChatMessageContextMenu
                                    message={message}
                                    dialogId={workspace.dialog?.id ?? ''}
                                    onSaved={workspace.refreshLive}
                                    enabled={canSend && workspace.dialog?.channel?.id === 'vox'
                                        && message.kind?.id !== ApiChatMessageKind.System}
                                >
                                    {(showMenu) => <article
                                        key={message.id}
                                        onContextMenu={showMenu}
                                        className={classNames(classes.message,
                                            message.kind?.id === ApiChatMessageKind.System
                                                ? classes.systemMessage
                                                : message.isOutgoing
                                                    ? classes.outgoing
                                                    : classes.incoming
                                        )}
                                        aria-label={message.kind?.id === ApiChatMessageKind.System
                                            ? 'Служебное уведомление'
                                            : message.isOutgoing
                                                ? 'Исходящее сообщение'
                                                : 'Входящее сообщение'
                                        }
                                    >
                                        <strong className={classes.sender}>
                                            {message.kind?.id === ApiChatMessageKind.System
                                                ? 'Служебное уведомление'
                                                : message.authorName || message.senderName}
                                        </strong>
                                        {message.text && (
                                            <p className={classes.messageText}>
                                                <MessageText text={getChatDialogMessageText(message)} />
                                            </p>
                                        )}
                                        {[...(message.attachments ?? [])]
                                            .sort((left, right) => (left.position ?? 0) - (right.position ?? 0))
                                            .map((attachment) => (
                                                <ChatDialogAttachment
                                                    key={attachment.id}
                                                    attachment={attachment}
                                                />
                                            ))}
                                        <footer className={classes.messageMetadata}>
                                            {showMenu &&
                                                <button
                                                    type="button"
                                                    className={classes.messageMenuButton}
                                                    aria-label="Действия с сообщением"
                                                    aria-haspopup="menu"
                                                    onClick={showMenu}
                                                >
                                                    <i
                                                        className="pi pi-ellipsis-h"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            }
                                            {message.kind?.id !== ApiChatMessageKind.System && message.isOutgoing && (
                                                <i
                                                    className={classNames('pi pi-arrow-up-right', classes.outgoingIndicator)}
                                                    role="img"
                                                    aria-label="Исходящее сообщение"
                                                    title="Исходящее сообщение"
                                                />
                                            )}
                                            <time dateTime={message.sentAt || message.receivedAt}>
                                                {formatMoscowTime(message.sentAt || message.receivedAt)}
                                            </time>
                                            {message.editedAt && workspace.dialog?.channel?.id === 'vox' && workspace.dialog.id && message.id &&
                                                <ChatMessageHistoryButton
                                                    dialogId={workspace.dialog.id}
                                                    messageId={message.id}
                                                    ownerAppUserId={historyOwnerAppUserId}
                                                />
                                            }
                                            {message.kind?.id !== ApiChatMessageKind.System && message.isOutgoing && (
                                                <span>
                                                    {workspace.dialog?.channel?.id === 'vox'
                                                        ? 'Сохранено'
                                                        : message.sendStatus?.name}
                                                </span>
                                            )}
                                            {message.kind?.id !== ApiChatMessageKind.System && message.isOutgoing && message.readState?.id === ApiChatReadState.Read && (
                                                <span>Прочитано</span>
                                            )}
                                        </footer>
                                        {message.kind?.id !== ApiChatMessageKind.System && message.isOutgoing
                                            && workspace.dialog?.channel?.id !== 'vox'
                                            && (message.sendStatus?.id === ApiChatMessageSendStatus.Unknown
                                                || message.sendStatus?.id === ApiChatMessageSendStatus.Failed) && (
                                                <ChatMessageSendProblem
                                                    key={message.retryAttemptId}
                                                    message={message}
                                                    dialogId={workspace.dialog?.id ?? ''}
                                                    canSend={canSend}
                                                />
                                            )}
                                        {message.id === displayBoundary?.messageId && (
                                            <span
                                                ref={readTargetRef}
                                                className={classes.readMarker}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </article>}
                                </ChatMessageContextMenu>
                            </Fragment>
                        ))}
                        {workspace.nextAfterSequence !== null && (
                            <div className={classes.pagination}>
                                <ActionButton
                                    type="button"
                                    label="Следующие сообщения"
                                    loading={isLoadingHistory}
                                    disabled={isLoadingHistory}
                                    onClick={() => {
                                        pinnedToBottom.current = false;
                                        void workspace.fetchNewerMessages();
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={classes.conversationFooter}>
                <RequestErrorBanner
                    status={workspace.readStatus}
                    error={workspace.readError}
                    fallbackMessage="Не удалось подтвердить прочтение"
                    retryLabel="Повторить прочтение"
                    onRetry={canRead && boundary
                        ? () => workspace.markRead(boundary.messageId)
                        : undefined
                    }
                />
                {canSend && workspace.dialog?.id && (
                    <ChatDialogComposer
                        templateMenuRef={templateMenuRef}
                        key={workspace.dialog.id}
                        dialogId={workspace.dialog.id}
                        disabled={workspace.messagesStatus !== RequestStatus.Success}
                    />
                )}
            </div>
        </section>
    );
};
